import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTickets } from "../context/TicketsContext";
import { festival } from "../data/festival";
import { IconClose, IconExternal } from "./icons";
import styles from "./TicketsOverlay.module.css";

// Iframe-modal that loads the Humanitix tickets page over a blurred
// version of the site. Renders to document.body via portal so it can't
// be trapped inside any ancestor's transform/filter containing block.
//
// Humanitix may or may not allow framing — the prominent
// "Open in Humanitix" link in the bar is the bail-out path if the
// iframe ends up blank due to X-Frame-Options.
export default function TicketsOverlay() {
  const { isOpen, close } = useTickets();

  // Lock body scroll + ESC-to-close while open.
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.root} role="dialog" aria-modal="true" aria-label="Buy tickets">
      <button
        type="button"
        className={styles.scrim}
        onClick={close}
        aria-label="Close tickets"
      />
      <div className={styles.frame}>
        <header className={styles.bar}>
          <span className={styles.brand}>
            <span className={styles.brandTop}>Tickets</span>
            <span className={styles.brandBot}>Rhythms on the Rio · 2026</span>
          </span>
          <a
            className={styles.openExternal}
            href={festival.ticketsUrl}
            target="_blank"
            rel="noopener"
          >
            <span>Open in new tab</span>
            <IconExternal />
          </a>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={close}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </header>
        <iframe
          className={styles.iframe}
          src={festival.ticketsUrl}
          title="Buy Rhythms on the Rio 2026 tickets — Humanitix"
          loading="eager"
          allow="payment *; clipboard-write"
        />
      </div>
    </div>,
    document.body
  );
}
